import type { Drawing } from '../api';
import { isSelectable, type DrawingDocument, type NodeId } from '../model';
import type { ResolvedEntity } from '../scene';
import { DEFAULT_SNAP_SETTINGS, resolvePoint, type SnapKind, type SnapSettings } from '../snap';
import { assertNever } from '../assert-never';
import { resolveKey, type KeyContext, type KeyInput } from './keys';
import { TOOLS, type ToolId } from './tools';
import type { InteractionState, PointerInput, Tool, ToolContext, ToolEvent } from './types';

/** How near, in screen pixels, the pointer must be to pick something. */
export const PICK_TOLERANCE_PX = 6;

export type InteractionListener = (state: InteractionState) => void;

/** A key the session claimed: whether to suppress the browser's default. */
export interface KeyOutcome {
  readonly preventDefault: boolean;
}

/**
 * Headless interaction: the active tool, selection, hover and snapping, all in world
 * coordinates. It never edits the drawing; a drawing's script is its only source of truth. A UI adapter feeds it pointer input and renders its state; tests drive
 * it directly.
 */
export class InteractionSession {
  private current: InteractionState;
  private readonly listeners = new Set<InteractionListener>();
  private readonly tools: ReadonlyMap<ToolId, Tool>;
  private readonly context: ToolContext;
  /** The last pointer event on the canvas, replayed when a snap setting changes. */
  private lastInput: PointerInput | null = null;
  private tolerance = { pick: 0 };
  private depth = 0;
  private dirty = false;
  /** Ortho turned on by holding Shift, so releasing Shift turns it off again. */
  private orthoHeld = false;
  private shown: DrawingDocument;

  constructor(private readonly drawing: Drawing) {
    this.tools = new Map(TOOLS.map((definition) => [definition.id, definition.create()]));
    const first = TOOLS[0].id;
    this.current = {
      toolId: first,
      hint: this.toolFor(first).status(),
      status: '',
      selection: [],
      hoveredId: null,
      cursor: null,
      snap: null,
      previews: [],
      measurements: [],
      marquee: null,
      region: null,
      placingPoint: this.toolFor(first).placingPoint(),
      inProgress: false,
      snapSettings: DEFAULT_SNAP_SETTINGS,
      panHeld: false,
      ...modesOf(this.toolFor(first)),
    };
    this.context = this.createContext();
    this.shown = drawing.document;
    drawing.subscribe(() => this.documentChanged());
  }

  get state(): InteractionState {
    return this.current;
  }

  subscribe(listener: InteractionListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get selectedEntities(): ResolvedEntity[] {
    const { selection } = this.current;
    return this.drawing.scene.entities.filter((entity) => selection.includes(entity.id));
  }

  /** Runs what a key does, or returns null when no binding claims it. See `keys.ts`. */
  keyDown(input: KeyInput): KeyOutcome | null {
    const binding = resolveKey(input, this.keyContext());
    if (!binding) return null;
    const { command } = binding;
    switch (command.kind) {
      case 'holdOrtho':
        this.orthoHeld = true;
        this.setOrtho(true);
        break;
      case 'holdPan':
        if (!this.current.panHeld) this.patch({ panHeld: true });
        break;
      case 'snap':
        this.chooseSnap(command.snap);
        break;
      case 'escape':
        this.escape();
        break;
      case 'toggleOrtho':
        this.setOrtho(!this.current.snapSettings.ortho);
        break;
      case 'cycleToolMode':
        this.cycleToolMode();
        break;
      case 'tool':
        this.setTool(command.tool);
        break;
      case 'none':
        break;
      default:
        assertNever(command);
    }
    return { preventDefault: binding.preventDefault };
  }

  keyUp(input: KeyInput): void {
    if (input.key === 'Shift' && this.orthoHeld) {
      this.orthoHeld = false;
      this.setOrtho(false);
    }
    if (input.key === ' ' && this.current.panHeld) this.patch({ panHeld: false });
  }

  /** What a right click does: abandons whatever the active tool is part-way through. */
  contextMenu(): void {
    this.cancel();
  }

  pointerDown(input: PointerInput): void {
    this.dispatch(input, (tool, event) => tool.pointerDown(this.context, event));
  }

  pointerMove(input: PointerInput): void {
    this.dispatch(input, (tool, event) => tool.pointerMove(this.context, event));
  }

  pointerUp(input: PointerInput): void {
    this.dispatch(input, (tool, event) => tool.pointerUp(this.context, event));
  }

  pointerLeave(): void {
    this.lastInput = null;
    this.patch({ cursor: null, snap: null, hoveredId: null });
  }

  setTool(id: ToolId): void {
    if (id === this.current.toolId) return;
    const next = this.toolFor(id);
    this.batch(() => {
      this.tool.cancel(this.context);
      next.activate(this.context);
      this.patch({
        toolId: id,
        previews: [],
        measurements: [],
        hoveredId: null,
        marquee: null,
        region: next.region(),
        status: '',
        hint: next.status(),
        placingPoint: next.placingPoint(),
        inProgress: next.inProgress(),
        ...modesOf(next),
      });
    });
  }

  /** Puts the active tool in another of its modes; ignored part-way through, or if unknown. */
  setToolMode(id: string): void {
    const { tool } = this;
    if (tool.inProgress() || !tool.modes().some((mode) => mode.id === id)) return;
    this.batch(() => {
      tool.setMode(id);
      this.patch({ ...modesOf(tool), hint: tool.status() });
      if (this.lastInput) this.pointerMove(this.lastInput);
    });
  }

  /** What Tab does: moves the active tool on to its next mode. */
  cycleToolMode(): void {
    const modes = this.tool.modes();
    const index = modes.findIndex((mode) => mode.id === this.tool.mode());
    const next = modes[(index + 1) % modes.length];
    if (next) this.setToolMode(next.id);
  }

  /**
   * What Escape does, one step per press: abandons what the active tool is part-way through,
   * then returns to the first tool, then clears the selection.
   */
  escape(): void {
    const home = TOOLS[0].id;
    if (this.tool.inProgress()) this.cancel();
    else if (this.current.toolId !== home) this.setTool(home);
    else this.select([]);
  }

  /** Abandons whatever the active tool is part-way through. */
  cancel(): void {
    this.batch(() => {
      this.tool.cancel(this.context);
      this.patch({
        previews: [],
        measurements: [],
        marquee: null,
        region: this.tool.region(),
        hoveredId: null,
        hint: this.tool.status(),
        placingPoint: this.tool.placingPoint(),
        inProgress: this.tool.inProgress(),
      });
    });
  }

  /** Starts over after a different document is loaded. */
  reset(): void {
    this.batch(() => {
      this.patch({ selection: [] });
      this.cancel();
    });
  }

  /** Entities on hidden or locked layers are left out. */
  select(ids: readonly NodeId[]): void {
    const { document } = this.drawing;
    this.patch({ selection: ids.filter((id) => isSelectable(document, id)) });
  }

  toggleSelected(id: NodeId): void {
    const { selection } = this.current;
    this.select(
      selection.includes(id) ? selection.filter((current) => current !== id) : [...selection, id],
    );
  }

  /** What a snap letter does: makes `kind` the one snap, or turns snapping off if it was. */
  chooseSnap(kind: SnapKind): void {
    this.setSnapKind(this.current.snapSettings.kind === kind ? null : kind);
  }

  setSnapKind(kind: SnapKind | null): void {
    this.setSnapSettings({ ...this.current.snapSettings, kind });
  }

  setOrtho(ortho: boolean): void {
    this.setSnapSettings({ ...this.current.snapSettings, ortho });
  }

  setGridSize(gridSize: number): void {
    if (gridSize > 0) this.setSnapSettings({ ...this.current.snapSettings, gridSize });
  }

  setAngleStep(angleStep: number): void {
    this.setSnapSettings({ ...this.current.snapSettings, angleStep });
  }

  private keyContext(): KeyContext {
    return {
      placingPoint: this.current.placingPoint,
      atFirstTool: this.current.toolId === TOOLS[0].id,
      ortho: this.current.snapSettings.ortho,
      toolModes: this.current.toolModes.length > 0,
    };
  }

  private get tool(): Tool {
    return this.toolFor(this.current.toolId);
  }

  private toolFor(id: ToolId): Tool {
    const tool = this.tools.get(id);
    if (!tool) throw new Error(`Unknown tool "${id}"`);
    return tool;
  }

  /** Re-snaps where the pointer rests, so a new setting shows at once. */
  private setSnapSettings(snapSettings: SnapSettings): void {
    this.batch(() => {
      this.patch({ snapSettings });
      if (this.lastInput) this.pointerMove(this.lastInput);
    });
  }

  private dispatch(input: PointerInput, run: (tool: Tool, event: ToolEvent) => void): void {
    this.lastInput = input;
    this.batch(() => {
      const event = this.resolve(input);
      run(this.tool, event);
      this.patch({
        previews: this.tool.previews(event.point),
        measurements: this.tool.measurements(),
        region: this.tool.region(),
        hint: this.tool.status(),
        placingPoint: this.tool.placingPoint(),
        inProgress: this.tool.inProgress(),
      });
    });
  }

  /** Snaps the pointer and sizes tolerances from the zoom it arrived at. */
  private resolve(input: PointerInput): ToolEvent {
    this.tolerance = { pick: PICK_TOLERANCE_PX / input.scale };

    const outcome = this.tool.placingPoint()
      ? resolvePoint({
          pointer: input.world,
          scale: input.scale,
          entities: this.drawing.scene.entities,
          reference: this.tool.reference(),
          settings: this.current.snapSettings,
          excludeIds: new Set(),
          ignorePoints: [],
          constructions: this.tool.constructions(),
        })
      : { point: input.world, snap: null };

    this.patch({ cursor: outcome.point, snap: outcome.snap });
    return {
      point: outcome.point,
      raw: input.world,
      snap: outcome.snap,
      button: input.button,
      shift: input.shift,
    };
  }

  private createContext(): ToolContext {
    const state = () => this.current;
    const selected = () => this.selectedEntities;
    const tolerance = () => this.tolerance;
    return {
      drawing: this.drawing,
      get selection() {
        return state().selection;
      },
      get selectedEntities() {
        return selected();
      },
      get tolerance() {
        return tolerance();
      },
      select: (ids) => this.select(ids),
      toggleSelected: (id) => this.toggleSelected(id),
      hoverEntity: (hoveredId) => this.patch({ hoveredId }),
      showMarquee: (marquee) => this.patch({ marquee }),
      report: (status) => this.patch({ status }),
    };
  }

  /**
   * Keeps the selection on what exists after a script re-run: ids that no longer exist, or
   * whose layer is hidden or locked, are dropped. Ids are stable across runs, so the rest
   * stay selected.
   */
  private documentChanged(): void {
    const doc = this.drawing.document;
    if (doc === this.shown) return;
    this.shown = doc;
    const selection = this.current.selection.filter((id) => isSelectable(doc, id));
    const { hoveredId } = this.current;
    if (hoveredId !== null && !isSelectable(doc, hoveredId)) this.patch({ hoveredId: null });
    if (!sameIds(selection, this.current.selection)) this.patch({ selection });
  }

  /** Coalesces every change made inside `fn` into one notification. */
  private batch(fn: () => void): void {
    this.depth += 1;
    try {
      fn();
    } finally {
      this.depth -= 1;
      if (this.depth === 0 && this.dirty) this.emit();
    }
  }

  private patch(next: Partial<InteractionState>): void {
    this.current = { ...this.current, ...next };
    this.dirty = true;
    if (this.depth === 0) this.emit();
  }

  private emit(): void {
    this.dirty = false;
    for (const listener of this.listeners) listener(this.current);
  }
}

const modesOf = (tool: Tool): Pick<InteractionState, 'toolModes' | 'toolMode'> => ({
  toolModes: tool.modes(),
  toolMode: tool.mode(),
});

const sameIds = (a: readonly NodeId[], b: readonly NodeId[]): boolean =>
  a.length === b.length && a.every((id, index) => id === b[index]);
